import { Modal, Button } from "react-bootstrap";
import React, { useState, useEffect } from "react";
import "../../modal.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { QUESTION_TYPES, SEVERITY_LEVELS } from "./../../enum.js";

const animatedComponents = makeAnimated();

const questionSchema = Yup.object({
    text: Yup.string().required("Question text is required"),
    type: Yup.number().required("Question type is required"),
    yesSeverityLevel: Yup.number().when("type", {
        is: QUESTION_TYPES.YES_NO,
        then: Yup.number().required("Yes severity level is required for Yes/No questions"),
    }),
    yesLink: Yup.string(),
    yesSpecialities: Yup.array().when("type", {
        is: QUESTION_TYPES.YES_NO,
        then: Yup.array().of(
            Yup.object().shape({
                value: Yup.string().required(),
                label: Yup.string().required(),
            })
        ).min(1, "At least one speciality is required for Yes/No questions"),
    }),
    noSeverityLevel: Yup.number().when("type", {
        is: QUESTION_TYPES.YES_NO,
        then: Yup.number().required("No severity level is required for Yes/No questions"),
    }),
    noLink: Yup.string(),
    noSpecialities: Yup.array().when("type", {
        is: QUESTION_TYPES.YES_NO,
        then: Yup.array().of(
            Yup.object().shape({
                value: Yup.string().required(),
                label: Yup.string().required(),
            })
        ).min(1, "At least one speciality is required for Yes/No questions"),
    }),
    options: Yup.array().when("type", {
        is: QUESTION_TYPES.MULTIPLE_CHOICE,
        then: Yup.array().of(
            Yup.object().shape({
                text: Yup.string().required("Option text is required"),
                severityLevel: Yup.number().required("Option severity level is required"),
                link: Yup.string(),
                specialities: Yup.array().of(
                    Yup.object().shape({
                        value: Yup.string().required(),
                        label: Yup.string().required(),
                    })
                ),
            })
        ).min(2, "At least two options are required for multiple-choice questions"),
    }),
});

const QuestionForm = ({ mode, showModal, hideModal, initialData = {}, onSuccess }) => {
    const isEditMode = mode === "edit";
    const [specialities, setSpecialities] = useState([]);
    const [firstAidGuides, setFirstAidGuides] = useState([]);

    useEffect(() => {
        const fetchSpecialities = async () => {
            try {
                const { data } = await axios.get("/speciality");
                setSpecialities(data.map((s) => ({ value: s.specialty_id, label: s.name })));
            } catch (error) {
                toast.error("Failed to fetch specialities.");
            }
        };
        const fetchGuides = async () => {
            try {
                const { data } = await axios.get("/first-aid-guides");
                setFirstAidGuides(data.map((g) => ({ value: g.id, label: g.title })));
            } catch (error) {
                toast.error("Failed to fetch first aid guides.");
            }
        };
        fetchSpecialities();
        fetchGuides();
    }, []);

    const onSubmit = async (values) => {
        try {
            const endpoint = isEditMode ? `/questions/${initialData.question_id}` : "/questions";
            const method = isEditMode ? axios.put : axios.post;

            const payload = {
                ...values,
                yesLink: values.yesLink ? String(values.yesLink) : "",
                noLink: values.noLink ? String(values.noLink) : "",
                options: values.options?.map((option) => ({
                    ...option,
                    link: option.link ? String(option.link) : "",
                    specialities: option.specialities.map((s) => s.value),
                })),
                yesSpecialities: values.yesSpecialities.map((s) => s.value),
                noSpecialities: values.noSpecialities.map((s) => s.value),
            };

            await method(endpoint, payload);

            toast.success(`Question ${isEditMode ? "updated" : "added"} successfully.`);
            onSuccess();
            hideModal();
        } catch (error) {
            const errorMsg = error.response?.data?.message || "An error occurred.";
            toast.error(`Failed to ${isEditMode ? "update" : "add"} question. ${errorMsg}`);
        }
    };

    const {
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldValue,
        isValid,
        resetForm,
    } = useFormik({
        initialValues: {
            text: initialData.text || "",
            type: initialData.type || "",
            yesSeverityLevel: initialData?.question_options?.find((o) => o.text === "Yes")?.severity_level ?? 0,
            yesLink: initialData?.question_options?.find((o) => o.text === "Yes")?.link ? Number(initialData?.question_options?.find((o) => o.text === "Yes")?.link) : "",
            noSeverityLevel: initialData?.question_options?.find((o) => o.text === "No")?.severity_level ?? 0,
            noLink: initialData?.question_options?.find((o) => o.text === "No")?.link ? Number(initialData?.question_options?.find((o) => o.text === "No")?.link) : "",
            options:
                initialData.type === QUESTION_TYPES.MULTIPLE_CHOICE
                    ? initialData.question_options?.map((o) => ({
                          text: o.text,
                          severityLevel: o.severity_level || "",
                          link: o.link ? Number(o.link) : "",
                          specialities: o.specialities.map((s) => ({
                              value: s.specialty_id,
                              label: s.name,
                          })),
                      })) || [
                          { text: "", severityLevel: 0, link: "", specialities: [] },
                          { text: "", severityLevel: 0, link: "", specialities: [] },
                      ]
                    : [],
            yesSpecialities: initialData?.question_options?.find((o) => o.text === "Yes")?.specialities.map((s) => ({
                value: s.specialty_id,
                label: s.name,
            })) || [],
            noSpecialities: initialData?.question_options?.find((o) => o.text === "No")?.specialities.map((s) => ({
                value: s.specialty_id,
                label: s.name,
            })) || [],
        },
        validationSchema: questionSchema,
        onSubmit
    });
    

    const addOption = () => {
        setFieldValue("options", [
            ...values.options,
            { text: "", severityLevel: 0, link: "", specialities: [] },
        ]);
    };

    const removeOption = (index) => {
        const updatedOptions = values.options.filter((_, i) => i !== index);
        setFieldValue("options", updatedOptions);
    };

    const handleOptionChange = (index, key, value) => {
        const updatedOptions = [...values.options];
        updatedOptions[index][key] = value;
        setFieldValue("options", updatedOptions);
    };

    const isSubmitDisabled = !isValid || (values.type === QUESTION_TYPES.MULTIPLE_CHOICE && values.options.length < 2);


    useEffect(() => {
        if (showModal && !isEditMode) {
            resetForm();
        }
    }, [showModal,isEditMode, resetForm]);

    return (
        <form onSubmit={handleSubmit} autoComplete="off">
            <Modal
                show={showModal}
                backdrop={false}
                onHide={hideModal}
                scrollable
                size="lg"
                style={{ marginTop: "60px", maxHeight: "90vh" }}
            >
                <Modal.Header closeButton>
                    <Modal.Title>{isEditMode ? "Edit Question" : "Add Question"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/* Question Text */}
                    <div className="form-group">
                        <label htmlFor="text">Question Text</label>
                        <input
                            type="text"
                            className="form-control"
                            id="text"
                            name="text"
                            value={values.text}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        {errors.text && touched.text && <div className="error">{errors.text}</div>}
                    </div>

                    {/* Question Type */}
                    <div className="form-group">
                        <label htmlFor="type">Question Type</label>
                        <select
                            className="form-control"
                            id="type"
                            name="type"
                            value={values.type}
                            onChange={(e) => setFieldValue("type", Number(e.target.value))}
                            onBlur={handleBlur}
                        >
                            <option value="">Select Type</option>
                            <option value={QUESTION_TYPES.YES_NO}>Yes/No</option>
                            <option value={QUESTION_TYPES.MULTIPLE_CHOICE}>Multiple Choice</option>
                        </select>
                        {errors.type && touched.type && <div className="error">{errors.type}</div>}
                    </div>

                    {/* Yes/No Options */}

                    {values.type === QUESTION_TYPES.YES_NO && (
                        <>
                            <h5>Yes Option</h5>
                            <div className="form-group row">
                                <div className="col-md-6">
                                    <label htmlFor="yesSeverityLevel">Severity Level</label>
                                    <select
                                        className="form-control"
                                        id="yesSeverityLevel"
                                        name="yesSeverityLevel"
                                        value={values.yesSeverityLevel}
                                        onChange={(e) => setFieldValue("yesSeverityLevel", Number(e.target.value))}
                                        onBlur={handleBlur}
                                    >
                                        <option value="">Select Severity Level</option>
                                        <option value={SEVERITY_LEVELS.MODERATE}>Moderate</option>
                                        <option value={SEVERITY_LEVELS.SEVERE}>Severe</option>
                                    </select>
                                    {errors.yesSeverityLevel && touched.yesSeverityLevel && (
                                        <div className="error">{errors.yesSeverityLevel}</div>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <label>First Aid Guide</label>
                                    <Select
                                        options={firstAidGuides}
                                        value={firstAidGuides.find(g => g.value === values.yesLink) || null}
                                        onChange={option => setFieldValue("yesLink", option ? option.value : "")}
                                        isClearable
                                        placeholder="Select First Aid Guide"
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Specialities</label>
                                <Select
                                    isMulti
                                    components={animatedComponents}
                                    options={specialities}
                                    value={values.yesSpecialities}
                                    onChange={(selected) =>
                                        setFieldValue("yesSpecialities", selected)
                                    }
                                    placeholder="Select specialities"
                                />
                                {errors.yesSpecialities && touched.yesSpecialities && (
                                    <div className="error">{errors.yesSpecialities}</div>
                                )}
                            </div>

                            <h5>No Option</h5>
                            <div className="form-group row">
                                <div className="col-md-6">
                                    <label htmlFor="noSeverityLevel">Severity Level</label>
                                    <select
                                        className="form-control"
                                        id="noSeverityLevel"
                                        name="noSeverityLevel"
                                        value={values.noSeverityLevel}
                                        onChange={(e) => setFieldValue("noSeverityLevel", Number(e.target.value))}
                                        onBlur={handleBlur}
                                    >
                                        <option value="">Select Severity Level</option>
                                        <option value={SEVERITY_LEVELS.MODERATE}>Moderate</option>
                                        <option value={SEVERITY_LEVELS.SEVERE}>Severe</option>
                                    </select>
                                    {errors.noSeverityLevel && touched.noSeverityLevel && (
                                        <div className="error">{errors.noSeverityLevel}</div>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <label>First Aid Guide</label>
                                    <Select
                                        options={firstAidGuides}
                                        value={firstAidGuides.find(g => g.value === values.noLink) || null}
                                        onChange={option => setFieldValue("noLink", option ? option.value : "")}
                                        isClearable
                                        placeholder="Select First Aid Guide"
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Specialities</label>
                                <Select
                                    isMulti
                                    components={animatedComponents}
                                    options={specialities}
                                    value={values.noSpecialities}
                                    onChange={(selected) =>
                                        setFieldValue("noSpecialities", selected)
                                    }
                                    placeholder="Select specialities"
                                />
                                {errors.noSpecialities && touched.noSpecialities && (
                                    <div className="error">{errors.noSpecialities}</div>
                                )}
                            </div>
                        </>
                    )}

                    {/* Multiple Choice Options */}
                    {values.type === QUESTION_TYPES.MULTIPLE_CHOICE && (
                        <>
                            <h5>Options</h5>
                            {values.options.map((option, index) => (
                                <div key={index} className="option-container mb-4">
                                    <div className="form-group">
                                        <label>Option Text</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={option.text}
                                            onChange={(e) =>
                                                handleOptionChange(index, "text", e.target.value)
                                            }
                                            onBlur={handleBlur}
                                        />
                                    </div>
                                    <div className="form-group row">
                                        <div className="col-md-6">
                                            <label>Severity Level</label>
                                            <select
                                                className="form-control"
                                                value={option.severityLevel}
                                                onChange={(e) =>
                                                    handleOptionChange(index, "severityLevel", Number(e.target.value))
                                                }
                                                onBlur={handleBlur}
                                            >
                                                <option value="">Select Severity Level</option>
                                                <option value={SEVERITY_LEVELS.MODERATE}>Moderate</option>
                                                <option value={SEVERITY_LEVELS.SEVERE}>Severe</option>
                                            </select>
                                        </div>
                                        <div className="col-md-6">
                                            <label>First Aid Guide</label>
                                            <Select
                                                options={firstAidGuides}
                                                value={firstAidGuides.find(g => g.value === option.link) || null}
                                                onChange={optionSelected => handleOptionChange(index, "link", optionSelected ? optionSelected.value : "")}
                                                isClearable
                                                placeholder="Select First Aid Guide"
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Specialities</label>
                                        <Select
                                            isMulti
                                            components={animatedComponents}
                                            options={specialities}
                                            value={option.specialities}
                                            onChange={(selected) =>
                                                handleOptionChange(index, "specialities", selected || [])
                                            }
                                            placeholder="Select..."
                                        />
                                        {errors.options && errors.options[index] && errors.options[index].specialities && (
                                            <div className="error">{errors.options[index].specialities}</div>
                                        )}
                                    </div>
                                    <Button
                                        variant="danger"
                                        onClick={() => removeOption(index)}
                                        className="mt-2"
                                    >
                                        Remove
                                    </Button>
                                </div>
                            ))}
                            <Button variant="secondary" onClick={addOption}>
                                Add Option
                            </Button>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={hideModal}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        onClick={handleSubmit}
                        disabled={isSubmitDisabled}
                    >
                        {isEditMode ? "Update" : "Submit"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </form>
    );
};

export default QuestionForm;
